import { Button, Group, rem } from '@mantine/core';
import EmployeeBasicEducationCard from './Cards/EmployeeBasicEducationCard';
import EmployeeEducationAttachmentsCard from './Cards/EmployeeEducationAttachmentsCard';
import EmployeeCoursesCard from './Cards/EmployeeCoursesCard';
import { createFormContext, zodResolver } from '@mantine/form';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import { isEmptyForm } from '@/utils/functions';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import useUpdateEmployee from '@/hooks/employee/useUpdateEmployee';
import useEmployee from '@/hooks/employee/useEmployee';
import useSyncFormWithEmployee from '@/hooks/employee/useSyncFormWithEmployee';
import {
  updateEmployeeSchema,
  type UpdateEmployee,
} from '@/schemas/API/PUT/employee/request';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { goodNotification } from '@/components/Notification/Notification';
import { displayError } from '@/utils/error/errorHandler';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';

export const [EmployeeFormProvider, useEmployeeFormContext, useEmployeeForm] =
  createFormContext<UpdateEmployee>();

const EmployeeEducationSection: React.FC = () => {
  const { employee, error } = useEmployee();
  const { updateEmployee } = useUpdateEmployee();

  const employeeForm = useEmployeeForm({
    validate: zodResolver(updateEmployeeSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useSyncFormWithEmployee(employee, employeeForm);

  if (error) return <SectionErrorNotification />;
  if (!employee) return <SectionLoadingOverlay />;

  const handleSave = async (): Promise<void> => {
    const validationResult = employeeForm.validate();
    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: `Formulář vzdělání se nepodařilo uložit.`,
        message: `Formulář obsahuje chyby.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }
    try {
      await updateEmployee(employee.id, employeeForm.values);
      goodNotification('Změny byly uloženy.');
    } catch (err) {
      displayError(err);
    }
  };

  const handleCancel = (): void => {
    window.location.reload();
  };

  if (!isEmptyForm(employeeForm))
    return (
      <>
        <EmployeeFormProvider form={employeeForm}>
          <Group justify="space-between" grow align="start" gap="sm">
            <EmployeeBasicEducationCard />
            <EmployeeCoursesCard />
          </Group>
        </EmployeeFormProvider>

        <EmployeeEducationAttachmentsCard />

        <ButtonBar>
          <Button
            data-testid="cancel button"
            variant="outline"
            radius="md"
            size="md"
            className="text-lg"
            onClick={handleCancel}
          >
            Storno
          </Button>
          <Button
            data-testid="save button"
            radius="md"
            size="md"
            className="text-lg"
            onClick={handleSave}
          >
            Uložit
          </Button>
        </ButtonBar>
      </>
    );

  return <SectionLoadingOverlay />;
};

export default EmployeeEducationSection;
